import { useWeb3ModalProvider } from "@web3modal/ethers/react";
import { BrowserProvider, Contract, Interface } from "ethers";
import { useEffect, useState } from "react";
import {
  fromDecimalData,
  getContractAddress,
  toDecimalData,
  validateFields,
} from "../common/helper";
import { useWallletContext } from "../hook/useWalletContext";
import STAKING_ABI from "../common/STAKING_ABI.json";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../states/ShowProgress";
import UPC_ABI from "../common/UPC_abi.json";
import * as yup from "yup";
import { toast } from "react-toastify";
import { METAMASK_ERROR_CODE } from "../common/const";

type Props = {
  apy: number;
  refreshFlag: boolean;
  handleRefreshFlag: any;
};

export const ContractDepositCard = ({
  apy,
  refreshFlag,
  handleRefreshFlag,
}: Props) => {
  const { walletProvider } = useWeb3ModalProvider();
  const { currentAddress, theme } = useWallletContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);

  const [balance, setBalance] = useState<number>(0);
  const [shouldDeposit, setShouldDeposit] = useState<number>(0);
  const [amount, setAmount] = useState<string>("");

  useEffect(() => {
    if (!currentAddress) {
      return;
    }

    if (!refreshFlag) {
      return;
    }

    getShouldDepositAmount();
    getBalance();
    handleRefreshFlag(false);
  }, [currentAddress, refreshFlag]);

  useEffect(() => {
    if (!currentAddress) {
      return;
    }
    getShouldDepositAmount();
    getBalance();
  }, [currentAddress]);

  // Balance and Should Deposit sum to the tier contract's total obligation.
  const getBalance = async () => {
    try {
      const provider = new BrowserProvider(walletProvider!);
      const signer = await provider.getSigner();

      const upc = new Contract(
        process.env.REACT_APP_UPC_CONTRACT!,
        new Interface(UPC_ABI).format(false),
        signer
      );

      setBalance(
        parseFloat(
          fromDecimalData(await upc.balanceOf(getContractAddress(apy)))
        )
      );
    } catch (err) {
      console.log(err);

      setBalance(0);
    }
  };

  const getShouldDepositAmount = async () => {
    try {
      const provider = new BrowserProvider(walletProvider!);
      const signer = await provider.getSigner();

      const staking = new Contract(
        getContractAddress(apy)!,
        new Interface(STAKING_ABI).format(false),
        signer
      );

      const shouldDepositAmount = parseFloat(
        fromDecimalData(await staking.getShouldDepositAmount())
      );

      setShouldDeposit(shouldDepositAmount);
      if (shouldDepositAmount !== 0) {
        setAmount(shouldDepositAmount.toString());
      }
    } catch (err) {
      console.log(err);

      setShouldDeposit(0);
      setAmount("");
    }
  };

  const handleDeposit = async () => {
    try {
      await validateFields(
        yup.object().shape({
          amount: yup
            .string()
            .required("Please input amount.")
            .matches(
              /^((\d*)|(\d+\.\d*)[^0\D])$/g,
              "Please input correct amount."
            ),
        }),
        ["amount"],
        [amount]
      );
    } catch (err: any) {
      toast.warn(err.message, { theme: theme });
      return;
    }

    if (parseFloat(amount) <= 0) {
      toast.warn("Should deposit greater than 0 UPC.", { theme: theme });
      return;
    }

    const provider = new BrowserProvider(walletProvider!);
    const signer = await provider.getSigner();

    const upc = new Contract(
      process.env.REACT_APP_UPC_CONTRACT!,
      new Interface(UPC_ABI).format(false),
      signer
    );

    setShowProgress(true);

    upc
      .transfer(getContractAddress(apy), toDecimalData(amount))
      .then(async (tx) => {
        await tx.wait();

        setShowProgress(false);
        toast.success("Deposit has been successful.", { theme: theme });
        handleRefreshFlag(true);
      })
      .catch((err) => {
        setShowProgress(false);

        if (err.code === METAMASK_ERROR_CODE.USER_REJECT) {
          return;
        }

        toast.error("Transaction reverted", { theme: theme });
      });
  };

  return (
    <div className="col-span-12">
      <div className="box ">
        <div className="box-header">
          <div className="sm:flex justify-between space-y-2 sm:space-y-0">
            <h5 className="box-title my-auto">Contract Deposit</h5>
          </div>
        </div>
        <div className="box-body">
          <div className="grid grid-cols-12 gap-x-5">
            <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex">
              <label
                htmlFor="inline-input-label-with-helper-text"
                className="ti-form-label my-auto text-md"
              >
                Balance:
              </label>
            </div>
            <div className="col-span-12 md:col-span-6 xxl:col-span-9">
              <label
                htmlFor="inline-input-label-with-helper-text"
                className="ti-form-label my-auto text-md text-end"
              >
                {balance.toFixed(5)} UPC
              </label>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-12 gap-x-5">
            <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex">
              <label
                htmlFor="inline-input-label-with-helper-text"
                className="ti-form-label my-auto text-md"
              >
                Should Deposit:
              </label>
            </div>
            <div className="col-span-12 md:col-span-6 xxl:col-span-9">
              <label
                htmlFor="inline-input-label-with-helper-text"
                className="ti-form-label my-auto text-md text-end"
              >
                {shouldDeposit.toFixed(5)} UPC
              </label>
            </div>
          </div>
          <div className="mt-2 grid grid-cols-12 gap-x-5">
            <div className="col-span-12 md:col-span-6 xxl:col-span-3 flex">
              <label
                htmlFor="inline-input-label-with-helper-text"
                className="ti-form-label my-auto text-md"
              >
                Deposit Amount:
              </label>
            </div>
            <div className="col-span-12 md:col-span-6 xxl:col-span-9">
              <input
                type="text"
                className="my-auto ti-form-input text-end"
                placeholder="Deposit Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-2 text-center">
            <button
              type="button"
              className="ti-btn ti-btn-primary"
              onClick={handleDeposit}
            >
              Deposit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
